/**
 * Hook for VDL/local-DB upload flow.
 * Extracted from useParsingWorkflow.ts (Phase 2.3 refactoring).
 *
 * Owns: uploading / uploadProgress / uploadResults / localDbResults state,
 *       handleUpload function, resetUpload helper.
 *
 * Receives: domains list (read-only) + a few other workflow values from the
 * facade. Calls the facade's setCurrentStep on success to advance the wizard.
 */

import { useCallback, useState } from 'react'
import { message } from 'antd'
import api from '@/services/api'
import { STEP_INDEX, UPLOAD_BATCH_SIZE } from '../constants'
import { checkIsPureDomain } from '../utils/domainHelpers'
import type { DomainWithAnalysis, UploadResult, LocalDbResult } from '../types'

interface UseUploadFlowProps {
  projectId?: number | null
  projectCrmId?: number | null
  domains: DomainWithAnalysis[]
  taskId: string | null
  activeBatchId: number | null
  manualSubsource: string
  setCurrentStep: (step: number) => void
  updateTaskStatus: (
    projectId: number,
    status: 'pending' | 'uploaded' | 'completed' | 'failed' | 'running',
    errorMessage?: string
  ) => void
  clearManualWorkflow: (projectId: number) => void
  clearActiveAnalysis: (projectId: number) => void
}

export interface UseUploadFlowResult {
  uploading: boolean
  uploadProgress: number
  uploadResults: UploadResult[]
  localDbResults: LocalDbResult[]
  setUploading: React.Dispatch<React.SetStateAction<boolean>>
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>
  setUploadResults: React.Dispatch<React.SetStateAction<UploadResult[]>>
  setLocalDbResults: React.Dispatch<React.SetStateAction<LocalDbResult[]>>
  handleUpload: () => Promise<void>
  /** Reset all upload state (called from resetState/handleStartOver) */
  resetUpload: () => void
}

export function useUploadFlow({
  projectId,
  projectCrmId,
  domains,
  taskId,
  activeBatchId,
  manualSubsource,
  setCurrentStep,
  updateTaskStatus,
  clearManualWorkflow,
  clearActiveAnalysis,
}: UseUploadFlowProps): UseUploadFlowResult {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([])
  const [localDbResults, setLocalDbResults] = useState<LocalDbResult[]>([])

  const handleUpload = useCallback(async () => {
    if (!projectCrmId) {
      message.error('CRM ID проекта не найден')
      return
    }

    const selectedItems = domains.filter((d) => d.selected)

    if (selectedItems.length === 0) {
      message.warning('Не выбрано ни одного источника для загрузки')
      return
    }

    // Separate pure domains (VDL) and pages/URLs (local DB only)
    const pureDomains = selectedItems.filter(
      (d) => d.source_type === 'domain' || (d.source_type === undefined && checkIsPureDomain(d.full_url))
    )
    const pagesOnly = selectedItems.filter(
      (d) => d.source_type === 'url' || (d.source_type === undefined && !checkIsPureDomain(d.full_url))
    )

    console.log(`📤 Upload: ${pureDomains.length} domains to VDL, ${pagesOnly.length} pages to local DB`)

    if (pureDomains.length === 0 && pagesOnly.length === 0) {
      message.warning('Нет источников для загрузки')
      return
    }

    if (pagesOnly.length > 0) {
      message.info(`${pagesOnly.length} страниц будут сохранены в локальную БД (не в VDL)`)
    }

    try {
      setUploading(true)
      setUploadProgress(0)
      setUploadResults([])
      setLocalDbResults([])

      // Save approved pages to parsed_urls table with AI analysis data
      if (pagesOnly.length > 0 && projectId) {
        try {
          const urlsWithAiData = pagesOnly.map((page) => ({
            url: page.full_url || `https://${page.host}/`,
            domain: page.host?.replace(/^https?:\/\//, '').replace(/\/$/, ''),
            path: page.full_url ? new URL(page.full_url).pathname : undefined,
            ai_recommendation: page.ai_recommendation,
            ai_score: page.ai_score,
            ai_reason: page.ai_reason,
            source_type: page.source_type || 'url',
          }))

          const saveResult = await api.saveApprovedUrls({
            project_id: projectId,
            urls: urlsWithAiData,
          })

          const localResults: LocalDbResult[] = pagesOnly.map((page) => {
            const savedUrl = saveResult.parsed_urls?.find(
              (u: { full_url?: string; id?: number }) =>
                u.full_url === (page.full_url || `https://${page.host}/`)
            )
            return {
              url: page.full_url || `https://${page.host}/`,
              host: page.host,
              success: true,
              parsed_url_id: savedUrl?.id || page.parsed_url_id,
            }
          })
          setLocalDbResults(localResults)

          console.log(
            `📦 Saved ${saveResult.saved_count} new + ${saveResult.skipped_count} existing pages to parsed_urls`
          )
        } catch (error: unknown) {
          console.error('Failed to save pages to local DB:', error)
          const localResults: LocalDbResult[] = pagesOnly.map((page) => ({
            url: page.full_url || `https://${page.host}/`,
            host: page.host,
            success: false,
            parsed_url_id: page.parsed_url_id,
            error: 'Не удалось сохранить',
          }))
          setLocalDbResults(localResults)
        }
      }

      // If no pure domains to upload to VDL, skip VDL upload
      if (pureDomains.length === 0) {
        message.success(`${pagesOnly.length} страниц сохранены в локальную БД`)
        setUploadProgress(100)
        setCurrentStep(STEP_INDEX.upload_results)
        if (projectId) {
          updateTaskStatus(projectId, 'uploaded')
        }
        setUploading(false)
        return
      }

      const domainHosts = pureDomains.map((d) => {
        try {
          const urlWithProtocol = d.host.startsWith('http') ? d.host : `https://${d.host}`
          const url = new URL(urlWithProtocol)
          return url.hostname
        } catch {
          return d.host.replace(/^https?:\/\//, '').replace(/\/$/, '')
        }
      })

      message.info(`Загрузка ${domainHosts.length} доменов в VDL...`)

      const results: UploadResult[] = []
      const batches: string[][] = []

      for (let i = 0; i < domainHosts.length; i += UPLOAD_BATCH_SIZE) {
        batches.push(domainHosts.slice(i, i + UPLOAD_BATCH_SIZE))
      }

      const totalBatches = batches.length
      let completedBatches = 0

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex]
        const batchStartIndex = batchIndex * UPLOAD_BATCH_SIZE

        try {
          setUploadProgress(Math.round((completedBatches / totalBatches) * 100))

          const response = await api.uploadDomainsToVDL({
            crm_id: projectCrmId,
            domains: batch,
            subsource: manualSubsource || `yandex_parsing_${taskId || 'manual'}`,
            task_id: taskId || undefined,
          })

          if (response.details && Array.isArray(response.details)) {
            response.details.forEach(
              (
                detail: {
                  success: boolean
                  is_duplicate?: boolean
                  error?: string
                  message?: string
                  source_id?: number
                },
                detailIndex: number
              ) => {
                const domainIndex = batchStartIndex + detailIndex
                results.push({
                  domain: domainHosts[domainIndex],
                  success: detail.success,
                  is_duplicate: detail.is_duplicate || false,
                  error: detail.error || detail.message,
                  vdl_id: detail.source_id,
                })
              }
            )
          } else {
            batch.forEach((_, detailIndex) => {
              const domainIndex = batchStartIndex + detailIndex
              results.push({
                domain: domainHosts[domainIndex],
                success: true,
                is_duplicate: false,
              })
            })
          }

          completedBatches++
          setUploadProgress(Math.round((completedBatches / totalBatches) * 100))
          setUploadResults([...results])

          if (batchIndex < batches.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200))
          }
        } catch (batchError: unknown) {
          console.error(`Batch ${batchIndex + 1} failed:`, batchError)

          const errMsg = batchError instanceof Error ? batchError.message : 'Ошибка загрузки батча'
          batch.forEach((_, detailIndex) => {
            const domainIndex = batchStartIndex + detailIndex
            results.push({
              domain: domainHosts[domainIndex],
              success: false,
              error: errMsg,
            })
          })

          completedBatches++
          setUploadProgress(Math.round((completedBatches / totalBatches) * 100))
          setUploadResults([...results])
        }
      }

      setUploadProgress(100)
      setUploadResults(results)

      const newlyAddedCount = results.filter((r) => r.success && !r.is_duplicate).length
      const duplicateCount = results.filter((r) => r.success && r.is_duplicate).length
      const failCount = results.filter((r) => !r.success).length
      const pagesLocalCount = pagesOnly.length

      const vdlSummary = `VDL: ${newlyAddedCount} новых${duplicateCount > 0 ? `, ${duplicateCount} дубликатов` : ''}${failCount > 0 ? `, ${failCount} ошибок` : ''}`
      const localSummary = pagesLocalCount > 0 ? ` | БД: ${pagesLocalCount} страниц` : ''

      if (failCount === 0 && duplicateCount === 0) {
        message.success(`✅ ${vdlSummary}${localSummary}`)
      } else if (failCount === 0) {
        message.info(`${vdlSummary}${localSummary}`)
      } else {
        message.warning(`${vdlSummary}${localSummary}`)
      }

      setCurrentStep(STEP_INDEX.upload_results)

      if (projectId) {
        updateTaskStatus(projectId, 'uploaded')
      }

      // Update workflow_step in database to 'uploaded' for state restoration
      let batchIdToUpdate = activeBatchId
      const crmIdForBatch = projectCrmId || projectId
      if (!batchIdToUpdate && crmIdForBatch) {
        try {
          const latestBatch = await api.getProjectLatestBatch(crmIdForBatch)
          if (latestBatch.has_batch && latestBatch.batch?.batch_id) {
            batchIdToUpdate = latestBatch.batch.batch_id
            console.log(`📍 Found latest batch #${batchIdToUpdate} for project ${crmIdForBatch}`)
          }
        } catch (e) {
          console.warn('Could not fetch latest batch for project:', e)
        }
      }

      if (batchIdToUpdate) {
        try {
          await api.updateBatchWorkflowStep(batchIdToUpdate, 'uploaded')
          console.log(`✅ Updated batch #${batchIdToUpdate} workflow_step to 'uploaded'`)
        } catch (error) {
          console.warn('Failed to update batch workflow_step:', error)
        }
      }

      // Clear localStorage workflow state after successful upload
      if (projectId) {
        clearManualWorkflow(projectId)
        clearActiveAnalysis(projectId)
        console.log(`🧹 Cleared workflow state for project ${projectId}`)
      }
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : 'Неизвестная ошибка'
      message.error('Ошибка загрузки в VDL: ' + errMsg)
      setUploadProgress(0)
    } finally {
      setUploading(false)
    }
  }, [
    projectCrmId,
    projectId,
    domains,
    taskId,
    activeBatchId,
    manualSubsource,
    setCurrentStep,
    updateTaskStatus,
    clearManualWorkflow,
    clearActiveAnalysis,
  ])

  const resetUpload = useCallback(() => {
    setUploading(false)
    setUploadProgress(0)
    setUploadResults([])
    setLocalDbResults([])
  }, [])

  return {
    uploading,
    uploadProgress,
    uploadResults,
    localDbResults,
    setUploading,
    setUploadProgress,
    setUploadResults,
    setLocalDbResults,
    handleUpload,
    resetUpload,
  }
}
